import ky from 'ky'

/**
 * 토큰 저장소 (localStorage - 탭 간 공유)
 */
const tokenStorage = {
  get: () => localStorage.getItem('accessToken') ? localStorage.getItem('accessToken') : sessionStorage.getItem('accessToken'),
  set: (token) => {
    try {
      localStorage.setItem('accessToken', token)
    } catch {
      sessionStorage.setItem('accessToken', token)
    }
  },
  clear: () => {
    try {
      localStorage.removeItem('accessToken')
    } catch {
      sessionStorage.removeItem('accessToken')
    }
  }
}

/**
 * 세션 유효 시간 (분)
 */
const SESSION_DURATION_MIN = 30

/**
 * 세션 만료 시간 갱신 (API 요청 시 호출)
 */
const extendSession = () => {
  const expireAt = Date.now() + SESSION_DURATION_MIN * 60 * 1000
  localStorage.setItem('sessionExpireAt', String(expireAt))
}

/**
 * 개발용 고정 토큰 (임시)
 * 각 앱에서 필요시 초기화하여 사용하세요
 */
// DEV_TOKEN은 각 앱의 devTokenManager.js 또는 유사 파일에서 관리합니다

// 기본 헤더 등 공통 설정을 포함한 baseApiClient를 생성합니다.
// 이 클라이언트는 prefixUrl이 없어, 사용하는 쪽에서 extend하여 사용합니다.
const baseApiClient = ky.create({
  // 인증 토큰을 요청 헤더에 자동으로 추가
  hooks: {
    beforeRequest: [
      request => {
        const token = tokenStorage.get()
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`)
          // API 요청 시 세션 연장
          extendSession()
        }
      }
    ],
    beforeError: [
      async (error) => {
        const { response } = error
        if (response) {
          try {
            const body = await response.clone().json()
            error.responseBody = body
            // 다양한 응답 형태에서 메시지 추출
            // { message }, { data: { message } }, { resultMessage }, { resultMsg }, 문자열 등
            error.serverMessage =
              body?.message ||
              body?.data?.message ||
              body?.error?.message ||
              body?.errorMessage ||
              body?.resultMessage ||
              body?.data?.resultMessage ||
              body?.resultMsg ||
              (typeof body === 'string' ? body : null)
          } catch {
            // JSON 파싱 실패 시 text로 시도
            try {
              const text = await response.clone().text()
              if (text) error.serverMessage = text
            } catch {
              // 무시
            }
          }
        }
        return error
      }
    ]
  }
})


/**
 * 토큰 설정 (로그인 후 호출)
 * @param {string} token - JWT Access Token
 */
export const setAccessToken = (token) => {
  tokenStorage.set(token)
}

/**
 * 저장된 토큰 조회
 * @returns {string|null} - 저장된 토큰 또는 null
 */
export const getAccessToken = () => {
  return tokenStorage.get()
}

/**
 * 토큰 제거 (로그아웃 시)
 */
export const clearAccessToken = () => {
  tokenStorage.clear()
}

/**
 * 에러 메시지를 사용자 친화적 메시지로 변환
 * - beforeError 훅은 HTTPError(4xx/5xx)에서만 실행됨
 * - TimeoutError 등 비-HTTP 에러는 beforeError를 거치지 않으므로
 *   catch 블록에서 이 함수를 사용하여 메시지를 변환
 * @param {string} message - 원본 에러 메시지
 * @returns {string} 변환된 메시지 (매칭 없으면 원본 반환)
 */
export const toUserFriendlyMessage = (message) => {
  if (!message) return message
  const userFriendlyMessages = [
    { match: 'Request timed out', message: '요청 처리 중 시간이 초과되었습니다. 다시 시도해주세요.' },
    { match: 'Request failed with status code 5', message: '서버 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' },
    { match: 'Internal Server Error', message: '서버 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' },
    { match: 'Request failed with status code', message: '요청 처리 중 오류가 발생했습니다. 다시 시도해주세요.' },
  ]
  const matched = userFriendlyMessages.find(({ match }) => message.includes(match))
  return matched ? matched.message : message
}

export default baseApiClient

